// Vehicle Year slider over the custom.era decade facet (snippets/decade-range-filter.liquid).
// The hidden decade checkboxes are the real filter. We tick every decade in range on each
// `input`, so they're already right when the range's `change` reaches facet-inputs-component.
// ⚠️ Horizon's on:change runs in document CAPTURE phase, i.e. before any listener here, which is
// why syncing on `change` does NOT work. Full range = nothing ticked = no filter.

class DecadeRangeSlider extends HTMLElement {
  connectedCallback() {
    this.lo = this.querySelector('[data-decade-lo]');
    this.hi = this.querySelector('[data-decade-hi]');
    this.boxes = [...this.querySelectorAll('.decade-range__values input')];
    if (!this.lo || !this.hi || this.boxes.length === 0) return;

    this.addEventListener('input', this.#onInput);
    this.querySelector('[data-decade-clear]')?.addEventListener('click', this.#onClear);
    this.#syncFromBoxes();
  }

  disconnectedCallback() {
    this.removeEventListener('input', this.#onInput);
  }

  get #last() {
    return this.boxes.length - 1;
  }

  // Server state lives on the checkboxes; re-read it in case a re-render morphed them.
  #syncFromBoxes() {
    const checked = this.boxes.map((b, i) => (b.checked ? i : -1)).filter((i) => i >= 0);
    this.lo.value = String(checked.length ? checked[0] : 0);
    this.hi.value = String(checked.length ? checked[checked.length - 1] : this.#last);
    this.#render();
  }

  #onInput = (event) => {
    if (event.target !== this.lo && event.target !== this.hi) return;
    // Handles may meet but not cross; whichever moved gets clamped.
    if (Number(this.lo.value) > Number(this.hi.value)) {
      if (event.target === this.lo) this.lo.value = this.hi.value;
      else this.hi.value = this.lo.value;
    }
    this.#render();
    this.#applyToBoxes();
  };

  #onClear = () => {
    this.lo.value = '0';
    this.hi.value = String(this.#last);
    this.#render();
    this.#applyToBoxes();
    // Nothing native fired, so raise the change facet-inputs-component listens for.
    this.boxes[0].dispatchEvent(new Event('change', { bubbles: true }));
  };

  #applyToBoxes() {
    const lo = Number(this.lo.value);
    const hi = Number(this.hi.value);
    const fullRange = lo === 0 && hi === this.#last;
    this.boxes.forEach((box, i) => {
      box.checked = !fullRange && i >= lo && i <= hi;
    });
  }

  #render() {
    const lo = Number(this.lo.value);
    const hi = Number(this.hi.value);
    const from = this.boxes[lo].dataset.from || this.boxes[lo].dataset.label;
    const to = this.boxes[hi].dataset.to || this.boxes[hi].dataset.label;
    const fullRange = lo === 0 && hi === this.#last;
    const text = `${from} – ${to}`;

    const readout = this.querySelector('[data-decade-readout]');
    if (readout) readout.textContent = text;

    const span = this.#last || 1;
    const fill = this.querySelector('[data-decade-fill]');
    if (fill) {
      fill.style.left = `${(lo / span) * 100}%`;
      fill.style.right = `${100 - (hi / span) * 100}%`;
    }

    const status = this.closest('details')?.querySelector('[data-decade-status]');
    if (status) status.textContent = fullRange ? '' : text.replace(' – ', '–');

    const clear = this.querySelector('[data-decade-clear]');
    if (clear) clear.hidden = fullRange;
  }
}

if (!customElements.get('decade-range-slider')) {
  customElements.define('decade-range-slider', DecadeRangeSlider);
}
